import { ProdantixError } from 'ProdantixSdk/core/errors';
import type { EventProperties } from 'ProdantixSdk/core/types';

export const ANNOTATION_ATTRIBUTE = 'data-prodantix';
export const ACTION_ATTRIBUTE = 'data-prodantix-action';
export const IGNORE_ATTRIBUTE = 'data-prodantix-ignore';
export const ANNOTATION_MAX_LENGTH = 300;

export const OPT_OUT_ATTRIBUTE = 'data-pdx-no-capture';
export const OPT_OUT_CLASS = 'pdx-no-capture';
const INTERACTIVE_TAGS = new Set(['a', 'button', 'input', 'label', 'option', 'select', 'summary', 'textarea']);
const INTERACTIVE_ROLES = new Set(['button', 'checkbox', 'link', 'menuitem', 'menuitemcheckbox', 'menuitemradio', 'option', 'radio', 'switch', 'tab', 'treeitem']);

export interface AnnotatedElement<T> {
  className: unknown;
  getAttribute(name: string): string | null;
  parentElement: T | null;
  tagName: string;
}

export type DescriptionSource = 'annotation' | 'aria_label' | 'text' | 'href' | 'role' | 'tag';

export function isInteractive<T>(element: AnnotatedElement<T>): boolean {
  return INTERACTIVE_TAGS.has(element.tagName.toLowerCase()) || INTERACTIVE_ROLES.has((element.getAttribute('role') ?? '').trim().toLowerCase());
}

export function resolveInteractive<T extends AnnotatedElement<T>>(target: T): T {
  for (let node: T | null = target; node !== null; node = node.parentElement) {
    if (isInteractive(node)) {
      return node;
    }
  }
  return target;
}

export function isIgnored<T extends AnnotatedElement<T>>(element: T): boolean {
  for (let node: T | null = element; node !== null; node = node.parentElement) {
    if (node.getAttribute(IGNORE_ATTRIBUTE) !== null) {
      return true;
    }
  }
  return false;
}

export function isOptedOut<T extends AnnotatedElement<T>>(element: T): boolean {
  for (let node: T | null = element; node !== null; node = node.parentElement) {
    if (node.getAttribute(OPT_OUT_ATTRIBUTE) !== null || (typeof node.className === 'string' && node.className.split(/\s+/).includes(OPT_OUT_CLASS))) {
      return true;
    }
  }
  return false;
}

export function reportOnce(onError: (error: unknown) => void): (error: ProdantixError) => void {
  const seen = new Set<string>();
  return error => {
    if (seen.has(error.message)) {
      return;
    }
    seen.add(error.message);
    onError(error);
  };
}

function readAnnotation<T>(element: AnnotatedElement<T>, name: string, report: (error: ProdantixError) => void): string | undefined {
  const raw = element.getAttribute(name);
  if (raw === null) {
    return undefined;
  }
  const value = raw.trim();
  if (value === '') {
    return undefined;
  }
  if (value.length > ANNOTATION_MAX_LENGTH) {
    report(new ProdantixError('validation', `${name} on a <${element.tagName.toLowerCase()}> is ${value.length} characters, over the ${ANNOTATION_MAX_LENGTH}-character limit, so it was ignored: "${value.slice(0, 40)}…"`));
    return undefined;
  }
  return value;
}

export function annotationProperties<T>(element: AnnotatedElement<T>, report: (error: ProdantixError) => void): EventProperties {
  const props: EventProperties = {};
  const description = readAnnotation(element, ANNOTATION_ATTRIBUTE, report);
  if (description !== undefined) {
    props.$el_description = description;
    props.$el_description_source = 'annotation' satisfies DescriptionSource;
  }
  const action = readAnnotation(element, ACTION_ATTRIBUTE, report);
  if (action !== undefined) {
    props.$el_action = action;
  }
  return props;
}

export function describeElement(element: unknown, report: (error: ProdantixError) => void): EventProperties {
  if (!isAnnotatedElement(element) || isIgnored(element) || isOptedOut(element)) {
    return {};
  }
  return annotationProperties(resolveInteractive(element), report);
}

interface DomElement {
  className: unknown;
  getAttribute(name: string): string | null;
  parentElement: DomElement | null;
  tagName: string;
}

function isAnnotatedElement(value: unknown): value is DomElement {
  return typeof value === 'object' && value !== null && typeof (value as { tagName?: unknown }).tagName === 'string' && typeof (value as { getAttribute?: unknown }).getAttribute === 'function';
}
